import React from 'react';
import { Phone, CheckCircle, MessageCircle, Wrench, Battery, Car } from 'lucide-react';
import { services, whatsappLink } from '../mock';

const Services = () => {
  const iconMap = {
    'Wrench': Wrench,
    'Battery': Battery,
    'Car': Car
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Hizmetlerimiz</h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Kadıköy bölgesinde 7/24 profesyonel mobil yol yardım hizmeti sunuyoruz. Her türlü acil durumda yanınızdayız.
          </p>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
                >
                  <div className="flex-1">
                    <div className="flex items-center justify-center w-48 h-48 bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl shadow-2xl mx-auto">
                      {IconComponent && <IconComponent className="w-24 h-24 text-white" />}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-600 mb-6">
                      {service.description}
                    </p>
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Hizmet Özellikleri</h3>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center space-x-2 bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-all transform hover:scale-105 shadow-lg"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>WhatsApp</span>
                      </a>
                      <a
                        href="tel:05534834264"
                        className="inline-flex items-center justify-center space-x-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-all transform hover:scale-105 shadow-lg"
                      >
                        <Phone className="w-5 h-5" />
                        <span>Hemen Ara</span>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Neden Bizi Tercih Etmelisiniz?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Hızlı Servis</h3>
              <p className="text-gray-600">
                Ortalama 20-30 dakika içinde yerinizde olup sorunlarınızı çözüyoruz.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Profesyonel Ekip</h3>
              <p className="text-gray-600">
                Deneyimli ve sertifikalı teknisyenlerimiz ile kaliteli hizmet sunuyoruz.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Uygun Fiyat</h3>
              <p className="text-gray-600">
                Şeffaf ve rekabetçi fiyatlandırma ile bütçenize uygun çözümler.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">7/24 Destek</h3>
              <p className="text-gray-600">
                Her gün, her saat arayabilir ve acil yardım alabilirsiniz.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Güvenilir</h3>
              <p className="text-gray-600">
                Yüzlerce müşterimizin güvenini kazanmış, referanslı bir firmayız.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Kaliteli Malzeme</h3>
              <p className="text-gray-600">
                Orijinal ve kaliteli yedek parçalar kullanarak uzun ömürlü çözümler sağlıyoruz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Hemen Yardım Almak İster misiniz?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Bir telefon kadar yakınız. 7/24 hizmetinizdeyiz!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-green-500 text-white px-10 py-5 rounded-lg font-bold text-xl hover:bg-green-600 transition-all transform hover:scale-105 shadow-2xl"
            >
              <MessageCircle className="w-7 h-7" />
              <span>WhatsApp</span>
            </a>
            <a
              href="tel:05534834264"
              className="inline-flex items-center space-x-3 bg-white text-orange-600 px-10 py-5 rounded-lg font-bold text-xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-2xl"
            >
              <Phone className="w-7 h-7" />
              <span>0553 483 42 64</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;